/**
 * Context Menu Manager
 * Handles context menu initialization and actions.
 * Extracted from main.js for better separation of concerns.
 */

import { saveMapExport, showExportWarnings } from "../ui/export-flow.js";
import { contextShortcut } from "../ui/shortcut-help.js";
import { t } from "../../../shared/i18n/index.js";

// Stored callbacks
let callbacks = null;

/**
 * Initialize context menu with node actions.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - contextMenuInit: () => void
 *   - contextMenuRegisterItems: (items) => void
 *   - isReadOnly: () => boolean
 *   - getState: () => AppState
 *   - get: (key) => any
 *   - findNode: (id) => Node
 *   - findParent: (id) => Node
 *   - addNode: (parentId, node) => void
 *   - removeNode: (id) => void
 *   - setFocus: (id) => void
 *   - openModal: (id) => void
 *   - switchView: (viewName) => void
 *   - showToast: (message, options) => void
 *   - getCurrentUser: () => User|null
 *   - copyNodes: (nodeId) => void
 *   - cutNodes: (nodeId) => void
 *   - pasteNodes: (parentId) => void
 *   - linkDocument: (nodeId) => void
 *   - unlinkDocument: (nodeId) => void
 *   - openDoc: (docId) => void
 *   - isNodeReadOnly: (nodeId) => boolean   // viewer role on the node's effective doc
 *   - isDeleteBlocked: () => boolean, isNodeDeleteBlocked: (nodeId) => boolean
 *     (the same, but a full map allows deleting and cutting)
 */
export function initContextMenu(cbs) {
  callbacks = cbs;

  callbacks.contextMenuInit();

  // Helper: a nested-reference or duplicate stub is inert — it has no
  // linked-doc content composited in this view. Most node actions should
  // hide or open the target doc instead of operating on the badge.
  const isNestedRefOrDup = (nodeId) => {
    const n = callbacks.findNode(nodeId);
    return !!(n && (n._nestedReference || n._duplicate));
  };

  // Combined read-only check: hides edit actions both when viewing a share
  // as viewer and when operating on linked-doc content the user only has
  // viewer role on. Falls back to plain isReadOnly for callers that don't
  // have the linked-aware callback.
  const isNodeReadOnly = (nodeId) => {
    if (callbacks.isReadOnly?.()) return true;
    return !!callbacks.isNodeReadOnly?.(nodeId);
  };
  // Cut and Delete: also allowed on a full map, to make room
  // (engine/services/map-size.js). Without the callbacks, the read-only check.
  const isNodeDeleteBlocked = (nodeId) => {
    if (!callbacks.isDeleteBlocked) return isNodeReadOnly(nodeId);
    return callbacks.isDeleteBlocked() || !!callbacks.isNodeDeleteBlocked?.(nodeId);
  };
  const shortcut = action => () => contextShortcut(action, callbacks.get('currentView'));

  callbacks.contextMenuRegisterItems([
    // === Open linked document standalone ===
    // Shown on any stub (top-level composited, nested, or duplicate badge).
    // For top-level stubs it's a convenience; for nested / duplicate badges
    // it's the only way to reach the target doc.
    {
      id: "open-linked-document",
      label: () => t("app.menu.openDocument"),
      icon: "\uD83D\uDCC2",
      condition: (nodeId) => {
        const n = callbacks.findNode(nodeId);
        return !!(n && n.linkedDocId);
      },
      action: (nodeId) => {
        const n = callbacks.findNode(nodeId);
        if (n?.linkedDocId && callbacks.openDoc) {
          callbacks.openDoc(n.linkedDocId);
        }
      },
    },
    // === Standard node actions ===
    {
      id: "add-daily-task",
      label: () => t("app.menu.addDailyTask"),
      icon: "☑",
      shortcut: shortcut('daily'),
      condition: (nodeId) => !isNodeReadOnly(nodeId) && !isNestedRefOrDup(nodeId),
      action: async (nodeId) => {
        if (isNodeReadOnly(nodeId) || isNestedRefOrDup(nodeId) || !callbacks.findNode(nodeId)) return;
        try { await callbacks.addNodeToDailyList?.(nodeId); }
        catch (error) { callbacks.showToast(error.message || t('app.menu.addTaskFailed'), { type: 'error' }); }
      },
    },
    {
      id: "edit",
      label: () => t("common.edit"),
      shortcut: shortcut('edit'),
      icon: "\u270F\uFE0F",
      condition: (nodeId) => !isNodeReadOnly(nodeId) && !isNestedRefOrDup(nodeId),
      action: (nodeId) => {
        callbacks.openModal(nodeId);
      },
    },
    {
      id: "add-child",
      label: () => t("app.menu.addChild"),
      shortcut: shortcut('add'),
      icon: "\u2795",
      condition: (nodeId) => !isNodeReadOnly(nodeId) && !isNestedRefOrDup(nodeId),
      action: (nodeId) => {
        callbacks.setFocus(nodeId);
        callbacks.openModal(null);
      },
    },
    {
      id: "zoom",
      label: () => t("app.menu.zoomInto"),
      icon: "\uD83D\uDD0D",
      // Hide if already focused on this node
      condition: (nodeId) => {
        const focusId = callbacks.get("focusId");
        return nodeId !== focusId;
      },
      action: (nodeId) => {
        callbacks.setFocus(nodeId);
        callbacks.switchView(callbacks.get("currentView"));
      },
    },
    {
      id: "related-notes",
      label: () => t("app.menu.relatedNotes"),
      icon: "📄",
      condition: (nodeId) => !isNestedRefOrDup(nodeId),
      action: (nodeId) => callbacks.showRelatedNotes?.(nodeId),
    },
    { separator: true },
    // === Clipboard actions ===
    {
      id: "copy",
      label: () => t("common.copy"),
      shortcut: shortcut('copy'),
      icon: "\uD83D\uDCCB",
      action: (nodeId) => {
        callbacks.copyNodes(nodeId);
      },
    },
    {
      id: "cut",
      label: () => t("common.cut"),
      shortcut: shortcut('cut'),
      icon: "\u2702\uFE0F",
      condition: (nodeId) => {
        if (isNodeDeleteBlocked(nodeId)) return false;
        if (isNestedRefOrDup(nodeId)) return false;
        const rootId = callbacks.getState().data?.id;
        return nodeId !== rootId && nodeId !== callbacks.get("focusId");
      },
      action: (nodeId) => {
        callbacks.cutNodes(nodeId);
      },
    },
    {
      id: "paste",
      label: () => t("common.paste"),
      shortcut: shortcut('paste'),
      icon: "\uD83D\uDCCE",
      // Available from a right-click on empty canvas too (target is the
      // currently-focused node, e.g. doc root in bubble view at top zoom).
      availableInEmptySpace: true,
      condition: (nodeId) => !isNodeReadOnly(nodeId) && !isNestedRefOrDup(nodeId),
      action: async (nodeId) => {
        callbacks.pasteNodes(nodeId);
      },
    },
    { separator: true },
    // === Document reference actions ===
    {
      id: "link-document",
      label: () => t("app.menu.linkDocument"),
      icon: "\uD83D\uDCC2",
      condition: (nodeId) => {
        if (isNodeReadOnly(nodeId)) return false;
        const state = callbacks.getState();
        if (state.isShared) return false;
        const node = callbacks.findNode(nodeId);
        if (node && node.linkedDocId) return false;
        if (isNestedRefOrDup(nodeId)) return false;
        if (!callbacks.getCurrentUser()) return false;
        // Inside linked content the link would be written into that other
        // map: only offer it on this map's own items.
        if (callbacks.getDocIdForNode(nodeId) !== callbacks.getCurrentDocId()) return false;
        return true;
      },
      action: (nodeId) => {
        if (callbacks.linkDocument) {
          callbacks.linkDocument(nodeId);
        }
      },
    },
    {
      id: "unlink-document",
      label: () => t("app.menu.unlinkDocument"),
      icon: "\uD83D\uDD13",
      condition: (nodeId) => {
        // Unlink writes to the host doc — the user needs write access there,
        // not on the linked content. Top-level stubs' host is main (always
        // writable for the user). Don't gate on isNodeReadOnly (which looks
        // at the linked target).
        // A duplicate link is the host's own item too (only links inside
        // linked content are not).
        const node = callbacks.findNode(nodeId);
        return !!(node && node.linkedDocId && !node._nestedReference);
      },
      action: (nodeId) => {
        if (callbacks.unlinkDocument) {
          callbacks.unlinkDocument(nodeId);
        }
      },
    },
    { separator: true },
    {
      id: "export-branch",
      label: () => t("app.menu.exportBranch"),
      icon: "\uD83D\uDCE4",
      action: async (nodeId) => {
        const node = callbacks.findNode(nodeId);
        if (!node) return;
        const cleaned = callbacks.cleanNode(node);
        const json = JSON.stringify(cleaned, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const safeName = (node.name || "branch").replace(/[^a-z0-9_\- ]/gi, "_").slice(0, 100);
        try {
          const saved = await saveMapExport(blob, `${safeName}.json`, { data: cleaned,
            docId: callbacks.getCurrentDocId?.(), allAttachments: false });
          if (saved && !saved.warnings.length) callbacks.showToast(t("app.menu.branchExported"), { type: "success" });
        } catch (error) { callbacks.showToast(t("app.menu.exportFailed", { error: error.message }), { type: "error" }); }
      },
    },
    {
      id: "import-branch",
      label: () => t("app.menu.importBranch"),
      icon: "\uD83D\uDCE5",
      availableInEmptySpace: true,
      condition: (nodeId) => !isNodeReadOnly(nodeId) && !isNestedRefOrDup(nodeId),
      action: (nodeId) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json,.zip";
        const sourceDocId = callbacks.getCurrentDocId?.();
        const targetDocId = callbacks.getDocIdForNode?.(nodeId) || sourceDocId;
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          try {
            const { readExportFile } = await import('../../../engine/crypto/data-export.js');
            const imported = await readExportFile(file);
            const data = imported.branchOnly ? imported.data : imported;
            if (!data?.name || data.data || data.documents || imported._markdownFile) throw new Error(t('app.menu.notBranchExport'));
            if (callbacks.getCurrentDocId?.() !== sourceDocId || isNodeReadOnly(nodeId)) throw new Error(t('app.menu.destinationChangedRetry'));
            let branch = data;
            let warnings = [];
            if (imported._attachmentArchive) {
              const { restoreMapAttachments } = await import('../../../engine/services/map-archive-import.js');
              const { remapAttachmentData } = await import('../../../shared/utils/map-archive.js');
              const archive = imported._attachmentArchive;
              const result = await restoreMapAttachments(archive, archive.manifest.documentIds[0], targetDocId, data);
              branch = remapAttachmentData(data, result.replacements);
              warnings = [...(archive.manifest.warnings || []), ...result.warnings];
            }
            if (callbacks.getCurrentDocId?.() !== sourceDocId || isNodeReadOnly(nodeId)) throw new Error(t('app.menu.destinationChangedZip'));
            callbacks.addNode(nodeId, callbacks.regenerateNodeIds(branch));
            callbacks.switchView(callbacks.get("currentView"));
            if (warnings.length) showExportWarnings(warnings);
            else callbacks.showToast(t("app.menu.branchImported"), { type: "success" });
          } catch (err) { callbacks.showToast(t("app.menu.importFailed", { error: err.message }), { type: "error" }); }
        };
        input.click();
      },
    },
    { separator: true },
    // === Danger zone ===
    {
      id: "delete",
      label: () => t("common.delete"),
      shortcut: shortcut('delete'),
      icon: "\uD83D\uDDD1\uFE0F",
      danger: true,
      condition: (nodeId) => {
        // Delete on a top-level stub is "unlink" (writes to host doc) —
        // allow even if the linked target is view-only. For everything else
        // require the node's effective doc to be writable.
        const node = callbacks.findNode(nodeId);
        const isTopLevelStub = !!(node && node.linkedDocId && !node._nestedReference);
        if (!isTopLevelStub && isNodeDeleteBlocked(nodeId)) return false;
        const rootId = callbacks.getState().data?.id;
        if (nodeId === rootId || nodeId === callbacks.get("focusId")) return false;
        // A link inside linked content belongs to that map: never deleted
        // from here. A duplicate link is this map's own item.
        if (node?._nestedReference) return false;
        return true;
      },
      action: (nodeId) => {
        const node = callbacks.findNode(nodeId);
        if (!node) return;
        const confirmed = confirm(t("app.menu.deleteConfirm", { name: node.name }));
        if (!confirmed) return;
        callbacks.removeNode(nodeId);
        callbacks.switchView(callbacks.get("currentView"));
        callbacks.showToast(t("app.menu.nodeDeleted"), { type: "success" });
      },
    },
  ]);
}
